/**
 * Super Admin Service
 * API calls for super admin operations
 */

import api from '../utils/api';
import { logger } from '../utils/logger';

const superAdminService = {
    /**
     * Get platform-wide statistics
     */
    getStats: async () => {
        try {
            const response = await api.get('/api/super-admin/stats');
            return response.data;
        } catch (error) {
            logger.error('Error fetching super admin stats:', error);
            throw error;
        }
    },

    /**
     * Get all tenants
     */
    getTenants: async () => {
        try {
            const response = await api.get('/api/super-admin/tenants');
            return response.data;
        } catch (error) {
            logger.error('Error fetching tenants:', error);
            throw error;
        }
    },

    /**
     * Get tenant by ID
     */
    getTenant: async (tenantId) => {
        try {
            const response = await api.get(`/api/super-admin/tenants/${tenantId}`);
            return response.data;
        } catch (error) {
            logger.error('Error fetching tenant:', error);
            throw error;
        }
    },

    /**
     * Create new tenant
     */
    createTenant: async (tenantData) => {
        try {
            const response = await api.post('/api/super-admin/tenants', tenantData);
            return response.data;
        } catch (error) {
            logger.error('Error creating tenant:', error);
            throw error;
        }
    },

    /**
     * Update tenant
     */
    updateTenant: async (tenantId, tenantData) => {
        try {
            const response = await api.put(`/api/super-admin/tenants/${tenantId}`, tenantData);
            return response.data;
        } catch (error) {
            logger.error('Error updating tenant:', error);
            throw error;
        }
    },

    /**
     * Delete tenant
     */
    deleteTenant: async (tenantId) => {
        try {
            const response = await api.delete(`/api/super-admin/tenants/${tenantId}`);
            return response.data;
        } catch (error) {
            logger.error('Error deleting tenant:', error);
            throw error;
        }
    },

    /**
     * Toggle tenant active status
     */
    toggleTenantStatus: async (tenantId) => {
        try {
            const response = await api.post(`/api/super-admin/tenants/${tenantId}/toggle-status`);
            return response.data;
        } catch (error) {
            logger.error('Error toggling tenant status:', error);
            throw error;
        }
    },

    /**
     * Get tenant usage statistics
     */
    getTenantUsage: async (tenantId) => {
        try {
            const response = await api.get(`/api/super-admin/tenants/${tenantId}/usage`);
            return response.data;
        } catch (error) {
            logger.error('Error fetching tenant usage:', error);
            throw error;
        }
    },

    /**
     * Get platform usage statistics
     */
    getPlatformUsage: async (params = {}) => {
        try {
            const response = await api.get('/api/super-admin/usage', { params });
            return response.data;
        } catch (error) {
            logger.error('Error fetching platform usage:', error);
            throw error;
        }
    },

    /**
     * Get all users across all tenants
     */
    getAllUsers: async () => {
        try {
            const response = await api.get('/api/super-admin/users');
            return response.data;
        } catch (error) {
            logger.error('Error fetching all users:', error);
            throw error;
        }
    },

    /**
     * Get users for specific tenant
     */
    getTenantUsers: async (tenantId) => {
        try {
            const response = await api.get(`/api/super-admin/tenants/${tenantId}/users`);
            return response.data;
        } catch (error) {
            logger.error('Error fetching tenant users:', error);
            throw error;
        }
    },

    /**
     * Update platform settings
     */
    updatePlatformSettings: async (settings) => {
        try {
            const response = await api.put('/api/super-admin/settings', settings);
            return response.data;
        } catch (error) {
            logger.error('Error updating platform settings:', error);
            throw error;
        }
    },

    /**
     * Get platform settings
     */
    getPlatformSettings: async () => {
        try {
            const response = await api.get('/api/super-admin/settings');
            return response.data;
        } catch (error) {
            logger.error('Error fetching platform settings:', error);
            throw error;
        }
    },

    // ============================================================================
    // Platform Settings - LLM & Embedding Configuration
    // ============================================================================

    /**
     * Get platform-wide LLM configuration
     */
    getLLMConfig: async () => {
        try {
            const response = await api.get('/api/platform-settings/llm/config');
            return response.data;
        } catch (error) {
            logger.error('Error fetching LLM config:', error);
            throw error;
        }
    },

    /**
     * Set platform-wide LLM configuration
     */
    setLLMConfig: async (config) => {
        try {
            const response = await api.post('/api/platform-settings/llm/config', config);
            return response.data;
        } catch (error) {
            logger.error('Error setting LLM config:', error);
            throw error;
        }
    },

    /**
     * Test LLM connection
     */
    testLLMConnection: async (config) => {
        try {
            const response = await api.post('/api/platform-settings/llm/test', config);
            return response.data;
        } catch (error) {
            logger.error('Error testing LLM connection:', error);
            throw error;
        }
    },

    /**
     * Get platform-wide embedding configuration
     */
    getEmbeddingConfig: async () => {
        try {
            const response = await api.get('/api/platform-settings/embedding/config');
            return response.data;
        } catch (error) {
            logger.error('Error fetching embedding config:', error);
            throw error;
        }
    },

    /**
     * Set platform-wide embedding configuration
     */
    setEmbeddingConfig: async (config) => {
        try {
            const response = await api.post('/api/platform-settings/embedding/config', config);
            return response.data;
        } catch (error) {
            logger.error('Error setting embedding config:', error);
            throw error;
        }
    },

    /**
     * Test embedding connection
     */
    testEmbeddingConnection: async (config) => {
        try {
            const response = await api.post('/api/platform-settings/embedding/test', config);
            return response.data;
        } catch (error) {
            logger.error('Error testing embedding connection:', error);
            throw error;
        }
    },

    // ============================================================================
    // Admin Management - Role Management
    // ============================================================================

    /**
     * Get all admin users (super_admin and sub_admin)
     */
    getAdmins: async () => {
        try {
            const response = await api.get('/api/admin-management/admins');
            return response.data;
        } catch (error) {
            logger.error('Error fetching admins:', error);
            throw error;
        }
    },

    /**
     * Create a new admin (super_admin or sub_admin)
     */
    createAdmin: async (adminData) => {
        try {
            const response = await api.post('/api/admin-management/admins', adminData);
            return response.data;
        } catch (error) {
            logger.error('Error creating admin:', error);
            throw error;
        }
    },

    /**
     * Get admin by ID
     */
    getAdmin: async (adminId) => {
        try {
            const response = await api.get(`/api/admin-management/admins/${adminId}`);
            return response.data;
        } catch (error) {
            logger.error('Error fetching admin:', error);
            throw error;
        }
    },

    /**
     * Update admin role (promote/demote)
     */
    updateAdminRole: async (adminId, role) => {
        try {
            const response = await api.patch(`/api/admin-management/admins/${adminId}/role`, { role });
            return response.data;
        } catch (error) {
            logger.error('Error updating admin role:', error);
            throw error;
        }
    },

    /**
     * Toggle admin active status
     */
    toggleAdminStatus: async (adminId) => {
        try {
            const response = await api.patch(`/api/admin-management/admins/${adminId}/toggle-active`);
            return response.data;
        } catch (error) {
            logger.error('Error toggling admin status:', error);
            throw error;
        }
    },

    /**
     * Get admin statistics
     */
    getAdminStats: async () => {
        try {
            const response = await api.get('/api/admin-management/stats');
            return response.data;
        } catch (error) {
            logger.error('Error fetching admin stats:', error);
            throw error;
        }
    },

    // ============================================================================
    // Dashboard - System Health, Activity Timeline, Recent Logins
    // ============================================================================

    /**
     * Get system health metrics (database, memory, storage, network)
     */
    getSystemHealth: async () => {
        try {
            const response = await api.get('/api/super-admin/system-health');
            return response.data;
        } catch (error) {
            logger.error('Error fetching system health:', error);
            throw error;
        }
    },

    /**
     * Get recent admin logins
     */
    getRecentLogins: async (limit = 10) => {
        try {
            const response = await api.get('/api/super-admin/recent-logins', {
                params: { limit }
            });
            return response.data;
        } catch (error) {
            logger.error('Error fetching recent logins:', error);
            throw error;
        }
    },

    /**
     * Get activity timeline (tenant creation, updates, admin logins)
     */
    getActivityTimeline: async (limit = 10) => {
        try {
            const response = await api.get('/api/super-admin/activity-timeline', {
                params: { limit }
            });
            return response.data;
        } catch (error) {
            logger.error('Error fetching activity timeline:', error);
            throw error;
        }
    },
};

export default superAdminService;
